import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth-store'
import { useShopStore } from '../stores/shop-store'
import { ALL_STORES_VALUE } from '../stores/auth-store'
import { IS_FREE_ACCESS_MODE } from '../config/freeAccess'
import { subscriptionRequiresPlanSelectionRef } from '../composables/useSubscription'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'onboarding',
    path: '/onboarding',
    component: () => import('../pages/onboarding/OnboardingPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    name: 'demo',
    path: '/demo',
    component: () => import('../pages/auth/DemoLogin.vue'),
    meta: { public: true },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'dashboard-all-stores',
        path: 'dashboard/all-stores',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'dashboard-customize-metrics',
        path: 'dashboard/customize-metrics',
        component: () => import('../pages/admin/dashboard/CustomizeMetricsPage.vue'),
        meta: { requiresUserAccount: true },
      },
      {
        name: 'dashboard-custom-metrics-manage',
        path: 'dashboard/custom-metrics',
        component: () => import('../pages/admin/dashboard/ManageCustomMetricsPage.vue'),
        meta: { requiresUserAccount: true },
      },
      {
        name: 'dashboard-custom-metrics-edit',
        path: 'dashboard/custom-metrics/:metricId?',
        component: () => import('../pages/admin/dashboard/EditCustomMetricPage.vue'),
        meta: { requiresUserAccount: true },
      },
      {
        name: 'orders',
        path: 'orders',
        component: () => import('../pages/orders/OrdersPage.vue'),
      },
      {
        name: 'customer-ltv',
        path: 'customer-ltv',
        component: () => import('../pages/admin/customer-ltv/CustomerLTVPage.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'setup-checklist',
        path: 'setup-checklist',
        component: () => import('../pages/SetupChecklist.vue'),
      },
      {
        name: 'settings-cogs',
        path: 'settings/cogs',
        component: () => import('../pages/cost-settings/product-cogs/ProductCostPage.vue'),
      },
      {
        name: 'settings-shipping-rule-new',
        path: 'settings/shipping/profile/:profileId/rule/new',
        component: () => import('../pages/cost-settings/shipping-cost/ShippingRuleRedirectToProfile.vue'),
        props: (r) => ({ profileId: String(r.params.profileId) }),
      },
      {
        name: 'settings-shipping-rule-edit',
        path: 'settings/shipping/profile/:profileId/rule/:ruleId/edit',
        component: () => import('../pages/cost-settings/shipping-cost/ShippingRuleRedirectToProfile.vue'),
        props: (r) => ({ profileId: String(r.params.profileId) }),
      },
      {
        path: 'settings/shipping',
        component: () => import('../pages/cost-settings/shipping-cost/ShippingCostPage.vue'),
        children: [
          {
            path: '',
            name: 'settings-shipping',
            component: () => import('../pages/cost-settings/shipping-cost/ShippingProfilesRulesTab.vue'),
          },
          {
            path: 'profile/:profileId/edit',
            name: 'settings-shipping-profile-edit',
            component: () => import('../pages/cost-settings/shipping-cost/ShippingProfileEditPage.vue'),
            props: (r) => ({ profileId: String(r.params.profileId) }),
            meta: { nestedUnderShippingTabs: true },
          },
          {
            path: 'zones/new',
            name: 'settings-shipping-zone-new',
            components: {
              zoneEditor: () => import('../pages/cost-settings/shipping-cost/ShippingZoneEditPage.vue'),
            },
            meta: { nestedUnderShippingTabs: true },
          },
          {
            path: 'zones/:zoneId/edit',
            name: 'settings-shipping-zone-edit',
            components: {
              zoneEditor: () => import('../pages/cost-settings/shipping-cost/ShippingZoneEditPage.vue'),
            },
            props: { zoneEditor: true },
            meta: { nestedUnderShippingTabs: true },
          },
        ],
      },
      {
        name: 'settings-fees',
        path: 'settings/fees',
        component: () => import('../pages/cost-settings/TransactionFeePage.vue'),
      },
      {
        name: 'settings-adspend',
        path: 'settings/adspend',
        component: () => import('../pages/settings/Settings.vue'), // Placeholder
      },
      {
        name: 'settings-custom-cost',
        path: 'settings/custom-cost',
        component: () => import('../pages/cost-settings/CustomCostPage.vue'),
      },
      {
        name: 'account-multi-store',
        path: 'account/multi-store',
        component: () => import('../pages/settings/Settings.vue'), // Placeholder
      },
      {
        name: 'user-details',
        path: 'user/details',
        component: () => import('../pages/user/UserDetails.vue'),
      },
      {
        name: 'user-profile',
        path: 'user/profile',
        component: () => import('../pages/user/UserProfile.vue'),
      },
      {
        name: 'change-password',
        path: 'user/change-password',
        component: () => import('../pages/user/ChangePassword.vue'),
      },
      {
        name: 'store-management',
        path: 'user/stores',
        component: () => import('../pages/user/StoreManagement.vue'),
      },
      {
        name: 'email-reports',
        path: 'email-reports',
        component: () => import('../pages/email-reports/EmailReportsPage.vue'),
      },
      {
        name: 'profit-loss',
        path: 'profit-loss',
        component: () => import('../pages/admin/profit-loss/ProfitLossPage.vue'),
      },
      {
        name: 'profit-loss-all-stores',
        path: 'profit-loss/all-stores',
        component: () => import('../pages/admin/profit-loss/ProfitLossPage.vue'),
      },
      {
        name: 'product-performance',
        path: '/product-report',
        component: () => import('../pages/admin/product-report/ProductReportPage.vue'),
      },
      {
        name: 'campaign-attribution',
        path: '/product-report/campaign-attribution',
        component: () => import('../pages/admin/product-report/campaign-attribution/CampaignAttributionPage.vue'),
      },
      {
        name: 'auto-match-suggestions',
        path: '/product-report/campaign-attribution/suggestions',
        component: () => import('../pages/admin/product-report/campaign-attribution/AutoMatchSuggestionsPage.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'cost-settings',
        path: '/cost-settings',
        component: RouteViewComponent,
        children: [
          {
            name: 'product-cost',
            path: 'product-cost',
            component: () => import('../pages/cost-settings/product-cogs/ProductCostPage.vue'),
          },
          {
            name: 'shipping-rule-new',
            path: 'shipping-cost/profile/:profileId/rule/new',
            component: () => import('../pages/cost-settings/shipping-cost/ShippingRuleRedirectToProfile.vue'),
            props: (r) => ({ profileId: String(r.params.profileId) }),
          },
          {
            name: 'shipping-rule-edit',
            path: 'shipping-cost/profile/:profileId/rule/:ruleId/edit',
            component: () => import('../pages/cost-settings/shipping-cost/ShippingRuleRedirectToProfile.vue'),
            props: (r) => ({ profileId: String(r.params.profileId) }),
          },
          {
            path: 'shipping-cost',
            component: () => import('../pages/cost-settings/shipping-cost/ShippingCostPage.vue'),
            children: [
              {
                path: '',
                name: 'shipping-cost',
                component: () => import('../pages/cost-settings/shipping-cost/ShippingProfilesRulesTab.vue'),
              },
              {
                path: 'profile/:profileId/edit',
                name: 'shipping-profile-edit',
                component: () => import('../pages/cost-settings/shipping-cost/ShippingProfileEditPage.vue'),
                props: (r) => ({ profileId: String(r.params.profileId) }),
                meta: { nestedUnderShippingTabs: true },
              },
              {
                path: 'zones/new',
                name: 'shipping-cost-zone-new',
                components: {
                  zoneEditor: () => import('../pages/cost-settings/shipping-cost/ShippingZoneEditPage.vue'),
                },
                meta: { nestedUnderShippingTabs: true },
              },
              {
                path: 'zones/:zoneId/edit',
                name: 'shipping-cost-zone-edit',
                components: {
                  zoneEditor: () => import('../pages/cost-settings/shipping-cost/ShippingZoneEditPage.vue'),
                },
                props: { zoneEditor: true },
                meta: { nestedUnderShippingTabs: true },
              },
            ],
          },
          {
            name: 'transaction-fee',
            path: 'transaction-fee',
            component: () => import('../pages/cost-settings/TransactionFeePage.vue'),
          },
          {
            name: 'custom-cost',
            path: 'custom-cost',
            component: () => import('../pages/cost-settings/CustomCostPage.vue'),
          },
        ],
      },

      {
        name: 'integrations',
        path: '/integrations',
        component: RouteViewComponent,
        children: [
          {
            name: 'marketing-channel',
            path: 'marketing-channel',
            component: () => import('../pages/integrations/MarketingChannelPage.vue'),
          },
          {
            name: 'marketing-channel-manage',
            path: 'marketing-channel/manage/:platformId',
            component: () => import('../pages/integrations/MarketingChannelManagePage.vue'),
            props: true,
          },
          {
            name: 'shipping-provider',
            path: 'shipping-provider',
            component: () => import('../pages/integrations/ShippingProviderPage.vue'),
          },
          {
            path: 'dropshipping-platform',
            redirect: { name: 'dropship-pod' },
          },
          {
            name: 'dropship-pod-connect',
            path: 'dropship-pod/connect',
            component: () => import('../pages/integrations/DropshipPodConnectPage.vue'),
          },
          {
            name: 'dropship-pod',
            path: 'dropship-pod',
            component: () => import('../pages/integrations/DropshipPodPage.vue'),
          },
        ],
      },

      {
        name: 'pricing-plans',
        path: '/pricing-plans',
        component: () => import('../pages/pricing-plans/PricingPlans.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'help',
        path: '/help',
        component: () => import('../pages/help/HelpPage.vue'),
      },
      {
        name: 'help-getting-started',
        path: '/help/getting-started',
        component: () => import('../pages/help/sections/GettingStarted.vue'),
      },
      {
        name: 'help-dashboard',
        path: '/help/dashboard',
        component: () => import('../pages/help/sections/Dashboard.vue'),
      },
      {
        name: 'help-orders',
        path: '/help/orders',
        component: () => import('../pages/help/sections/Orders.vue'),
      },
      {
        name: 'help-cost-settings',
        path: '/help/cost-settings',
        component: () => import('../pages/help/sections/CostSettings.vue'),
      },
      {
        name: 'help-integrations',
        path: '/help/integrations',
        component: () => import('../pages/help/sections/Integrations.vue'),
      },
      {
        name: 'help-subscription-plans',
        path: '/help/subscription-plans',
        component: () => import('../pages/help/sections/SubscriptionPlans.vue'),
      },
      {
        name: 'help-troubleshooting',
        path: '/help/troubleshooting',
        component: () => import('../pages/help/sections/Troubleshooting.vue'),
      },
      {
        name: 'help-contact',
        path: '/help/contact',
        component: () => import('../pages/help/sections/Contact.vue'),
      },
      {
        name: 'vuestic-cheatsheet',
        path: '/vuestic-cheatsheet',
        component: () => import('../components/VuesticCheatsheet.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { public: true, redirectIfAuthenticated: true },
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
        meta: { public: true, redirectIfAuthenticated: true },
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
        meta: { public: true, redirectIfAuthenticated: true },
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
        meta: { public: true, redirectIfAuthenticated: true },
      },
      {
        name: 'shopify-callback',
        path: 'callback',
        component: () => import('../pages/auth/ShopifyCallback.vue'),
        meta: { public: true },
      },
      {
        name: 'user-link-store',
        path: 'user/link-store',
        component: () => import('../pages/auth/user/LinkStore.vue'),
        meta: { public: true },
      },
      {
        name: 'user-login-current-shop',
        path: 'user/login-current-shop',
        component: () => import('../pages/auth/user/LoginCurrentShop.vue'),
        meta: { public: true },
      },
      {
        name: 'user-link-current-store',
        path: 'user/link-current-store',
        component: () => import('../pages/auth/user/LinkCurrentStore.vue'),
        meta: { public: true },
      },
      {
        name: 'shop-account-setup',
        path: 'user/shop-account-setup',
        component: () => import('../pages/auth/user/ShopAccountSetup.vue'),
        meta: {
          public: true,
          requiresShopToken: true,
          redirectIfUserToken: 'user-link-current-store',
        },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

const allStoresSupportedRoutes = new Set(['dashboard', 'dashboard-all-stores', 'profit-loss', 'profit-loss-all-stores'])

const getFallbackSingleStoreId = (authStore: ReturnType<typeof useAuthStore>): string | null => {
  const stores = Array.isArray(authStore.userStores) ? authStore.userStores : []
  if (!stores.length) {
    const shopId = localStorage.getItem('shop_id')
    return shopId || null
  }

  const storeIds = new Set(stores.map((s) => String(s.id)))
  const lastSingleStoreId = localStorage.getItem('last_single_store')
  if (lastSingleStoreId && storeIds.has(lastSingleStoreId)) return lastSingleStoreId

  const currentShopId = localStorage.getItem('shop_id')
  if (currentShopId && storeIds.has(currentShopId)) return currentShopId

  return String(stores[0].id)
}

const planSelectionRoutes = new Set(['pricing-plans', 'onboarding', 'shopify-callback', 'demo'])

// Navigation guard - Simple and stable
// NO ASYNC LOGIC - only checks cached data from stores
// Router starts AFTER bootstrap completes, so all data is available
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const shopStore = useShopStore()

  if (IS_FREE_ACCESS_MODE && to.path === '/pricing-plans') {
    return next({ name: 'dashboard' })
  }

  const isPublic = to.meta.public === true
  const requiresAuth = to.meta.requiresAuth === true || (!isPublic && to.matched.some((r) => r.meta.requiresAuth))

  const isAuthenticated = authStore.isAuthenticated

  // 1) Not authenticated -> only allow public routes
  if (requiresAuth && !isAuthenticated) {
    if (to.query.jwt) {
      return next({ name: 'shopify-callback', query: { jwt: to.query.jwt } })
    }
    return next({ name: 'login' })
  }

  // 2) Authenticated + JWT in URL -> always go to callback (unless already going there)
  if (isAuthenticated && to.query.jwt && to.name !== 'shopify-callback') {
    return next({ name: 'shopify-callback', query: { jwt: to.query.jwt } })
  }

  // 3) Authenticated -> block auth pages that should redirect
  if (isAuthenticated && to.meta.redirectIfAuthenticated) {
    // Exception: SHOP-token sessions may be forced to create a user account (e.g. customize metrics).
    // In that case we allow access to signup even though the session is "authenticated" via shop JWT.
    const forcingAccountCreation =
      authStore.tokenType === 'SHOP' &&
      to.name === 'signup' &&
      typeof to.query.redirect === 'string' &&
      to.query.redirect
    if (!forcingAccountCreation) {
      return next({ name: 'dashboard' })
    }
  }

  // 4) Shop-account-setup access rule (no service calls, store only)
  if (to.meta.requiresShopToken === true) {
    // You MUST expose this in shopStore synchronously, do not read localStorage here.
    if (!shopStore.hasShopToken) {
      return next({ name: 'login' })
    }

    if (authStore.isUserToken && to.meta.redirectIfUserToken) {
      return next({ name: to.meta.redirectIfUserToken as string })
    }
  }

  // 5) Post-auth routing rules (single place)
  if (isAuthenticated && requiresAuth) {
    // For USER tokens accessing dashboard/app routes, check if user has stores
    // If no stores, redirect to store-management (prevents access issues)
    const isInAuthSection = to.matched.some((r) => r.path.startsWith('/auth'))

    // Custom metrics are user-account features: shop-token sessions must create/login to a user account.
    const requiresUserAccount =
      to.meta.requiresUserAccount === true || to.matched.some((r) => r.meta.requiresUserAccount === true)
    if (!isInAuthSection && requiresUserAccount && authStore.tokenType === 'SHOP') {
      return next({
        name: 'shop-account-setup',
        query: { redirect: typeof to.fullPath === 'string' ? to.fullPath : undefined },
      })
    }

    if (!isInAuthSection && authStore.tokenType === 'USER') {
      const storesCount = authStore.user?.stores ?? 0
      if (storesCount === 0 && to.name !== 'store-management') {
        // User has no stores, redirect to store-management
        return next({ name: 'store-management' })
      }
    }

    // install flow
    if (shopStore.isInstallInProgress && to.name !== 'onboarding') {
      return next({ name: 'onboarding' })
    }
    if (shopStore.isInstallCompleted && to.name === 'onboarding') {
      return next({ name: 'dashboard' })
    }

    const hasShopContext =
      authStore.tokenType === 'USER' ? (authStore.user?.stores ?? 0) > 0 : shopStore.shopDetails !== null

    if (
      !IS_FREE_ACCESS_MODE &&
      hasShopContext &&
      subscriptionRequiresPlanSelectionRef.value &&
      !shopStore.isInstallInProgress &&
      !planSelectionRoutes.has(String(to.name))
    ) {
      return next({ name: 'pricing-plans' })
    }

    // shop token but shop belongs to a user -> require user login
    // IMPORTANT: Only enforce this for app routes, not auth routes.
    if (!isInAuthSection && authStore.tokenType === 'SHOP' && shopStore.shopDetails?.owned_account) {
      if (to.name !== 'user-login-current-shop') {
        return next({ name: 'user-login-current-shop' })
      }
    }

    // 6) "All Stores" scope is only supported on Dashboard + Profit & Loss pages.
    if (authStore.tokenType === 'USER') {
      const selectedStore = localStorage.getItem('selected_store')
      const routeName = String(to.name || '')
      const isAllStoresSelected = selectedStore === ALL_STORES_VALUE
      const isSupportedRoute = allStoresSupportedRoutes.has(routeName)
      if (isAllStoresSelected && !isSupportedRoute) {
        const fallbackStoreId = getFallbackSingleStoreId(authStore)
        if (fallbackStoreId) {
          localStorage.setItem('selected_store', fallbackStoreId)
          localStorage.setItem('shop_id', fallbackStoreId)
          localStorage.setItem('last_single_store', fallbackStoreId)

          const fallbackStoreName =
            authStore.userStores.find((s) => String(s.id) === String(fallbackStoreId))?.name ||
            `Store ${fallbackStoreId}`
          sessionStorage.setItem(
            'store-scope-switch-toast',
            JSON.stringify({
              storeName: fallbackStoreName,
              switchedAt: Date.now(),
            }),
          )
          authStore.syncSelectedStoreLocalIdFromStorage()
        }
      }
    }
  }

  return next()
})

export default router
